import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const InstituteConfirmationDialog = ({ isOpen, action, institute, onConfirm, onCancel, isLoading }) => {
  if (!isOpen || !institute) return null;

  const isApprove = action === 'approve';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-background rounded-2xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className={`flex items-center gap-3 p-5 border-b border-border rounded-t-2xl ${
          isApprove ? 'bg-success/5' : 'bg-error/5'
        }`}>
          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
            isApprove ? 'bg-success/15' : 'bg-error/15'
          }`}>
            <Icon
              name={isApprove ? 'CheckCircle' : 'XCircle'}
              size={22}
              color={isApprove ? 'var(--color-success)' : 'var(--color-error)'}
            />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">
              {isApprove ? 'Approve Institute' : 'Reject Institute'}
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {isApprove
                ? 'Please review the details before approving'
                : 'Please review the details before rejecting'}
            </p>
          </div>
          <button
            onClick={onCancel}
            className="ml-auto text-muted-foreground hover:text-foreground transition-colors p-1 rounded-lg hover:bg-muted"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Institute Details */}
        <div className="p-5 space-y-4">
          {/* Institute Name */}
          <div className="bg-muted/50 rounded-xl p-4 space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="Building2" size={18} color="var(--color-primary)" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-0.5">Institute Name</p>
                <p className="text-sm font-semibold text-foreground leading-snug">{institute?.name || 'N/A'}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-1">
              {/* Location */}
              <div className="flex items-start gap-2">
                <Icon name="MapPin" size={14} color="var(--color-muted-foreground)" className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-muted-foreground">Location</p>
                  <p className="text-xs font-medium text-foreground">{institute?.location || 'N/A'}</p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-2">
                <Icon name="Mail" size={14} color="var(--color-muted-foreground)" className="flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Email</p>
                  <p className="text-xs font-medium text-foreground truncate">{institute?.email || 'N/A'}</p>
                </div>
              </div>

              {/* Courses */}
              <div className="flex items-start gap-2">
                <Icon name="BookOpen" size={14} color="var(--color-muted-foreground)" className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-muted-foreground">Courses</p>
                  <p className="text-xs font-medium text-foreground">{institute?.coursesCount ?? 0}</p>
                </div>
              </div>

              {/* Applied Date */}
              <div className="flex items-start gap-2">
                <Icon name="Calendar" size={14} color="var(--color-muted-foreground)" className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-muted-foreground">Applied</p>
                  <p className="text-xs font-medium text-foreground">{institute?.appliedDate || 'N/A'}</p>
                </div>
              </div>

              {/* Booth Type */}
              {institute?.boothType && (
                <div className="flex items-start gap-2">
                  <Icon name="Store" size={14} color="var(--color-muted-foreground)" className="flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground">Booth Type</p>
                    <p className="text-xs font-medium text-foreground">{institute?.boothType}</p>
                  </div>
                </div>
              )}

              {/* Institute ID */}
              <div className="flex items-start gap-2">
                <Icon name="Hash" size={14} color="var(--color-muted-foreground)" className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-muted-foreground">ID</p>
                  <p className="text-xs font-mono text-foreground">{institute?.id?.substring(0, 8) || 'N/A'}...</p>
                </div>
              </div>
            </div>
          </div>

          {/* Warning message */}
          <div className={`flex items-start gap-2 p-3 rounded-lg border ${
            isApprove
              ? 'bg-success/5 border-success/20' :'bg-error/5 border-error/20'
          }`}>
            <Icon
              name={isApprove ? 'Info' : 'AlertTriangle'}
              size={15}
              color={isApprove ? 'var(--color-success)' : 'var(--color-error)'}
              className="flex-shrink-0 mt-0.5"
            />
            <p className={`text-xs leading-relaxed ${isApprove ? 'text-success' : 'text-error'}`}>
              {isApprove
                ? 'Approving will activate this institute account and allow them to access the platform.'
                : 'Rejecting will deny this institute access. This action can be reversed by re-approving later.'}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 p-5 pt-0">
          <Button
            variant="outline"
            className="flex-1"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant={isApprove ? 'default' : 'destructive'}
            className="flex-1"
            iconName={isApprove ? 'Check' : 'X'}
            onClick={onConfirm}
            loading={isLoading}
            disabled={isLoading}
          >
            {isApprove ? 'Yes, Approve' : 'Yes, Reject'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InstituteConfirmationDialog;
